// @flow

import { v4 } from 'uuid';
import queryString from 'query-string';
import React, { useRef } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import {
  LazyloadPlaceholder,
  Heading,
  Link,
  Button,
  SliderPagination,
  scrollToElement,
} from '@riseart/common';
import { Section, Wrapper } from '@riseart/layout';
import { GridSquare, GridSquareItem, GridPaginationWrapper } from '@riseart/grid';
import { CardArticle } from '@riseart/article';
import { article as ENUM_ARTICLE, meta as META_ENUM, pagination as ENUM_PAGINATION } from 'Enum';
import { Query } from 'shared_services/apollo/Query';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { Pagination } from 'shared_hocs/pagination/Pagination';
import { Breadcrumbs } from 'shared_components/common/breadcrumbs/Breadcrumbs';
import { Share } from 'shared_components/common/share/Share';
import {
  generateSrcForCDN,
  PicturePlaceholder,
} from 'shared_components/common/artdirection/picture/Picture';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { Boxes } from 'shared_components/common/boxes/Boxes';
import { BlogHeader } from 'shared_components/article/category/Header';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { META_SUBSCRIBER_NAME } from 'shared_services/riseart/meta/Meta';
import FILTER_ARTICLE_CATEGORIES from 'shared_data/queries/articles/filterCategories.graphql';
import FILTER_ARTICLES_QUERY_ONE from 'shared_data/queries/articles/filterArticlesByCategoryOne.graphql';
import FILTER_ARTICLES_QUERY_TWO from 'shared_data/queries/articles/filterArticlesByCategoryTwo.graphql';
import FILTER_ARTICLES_QUERY_THREE from 'shared_data/queries/articles/filterArticlesByCategoryThree.graphql';
import FILTER_ARTICLES_QUERY_FOUR from 'shared_data/queries/articles/filterArticlesByCategoryFour.graphql';

import {
  guidesHomeCls,
  guidesHomeArticleCls,
  guidesHomeActionCls,
} from 'shared_components/pages/guides/Home.less';

const LAZYLOAD_ARTICLE_CONFIG = {
  once: true,
  height: 175,
  offset: 200,
  placeholder: <LazyloadPlaceholder height={175} />,
};

const PRELOADER_SUBSCRIBE_ID = v4();

/**
 * GuidesHomePage
 */
export default function GuidesHomePage(): Node {
  const { formatMessage } = useIntl();
  const { translatedLocation: location } = useUrlParams();
  const { page = 1 } = queryString.parse(location.search);
  const topOfGridRef: React.RefObject<HTMLElement | null> = useRef(null);
  const scrollToTopOfGrid = () => scrollToElement(topOfGridRef);

  return (
    <LayoutDefault scrollTopOnPathChange className={guidesHomeCls}>
      <Query
        query={FILTER_ARTICLE_CATEGORIES}
        variables={{
          inputArticleCategoryFilter: {
            type: ENUM_ARTICLE.type.TYPE_GUIDE,
            page: 1,
            items: 6,
          },
        }}
      >
        {({ loading, data, error }) => {
          const { filterArticleCategories: { items: categories } = {} } = data || {};

          if (error) {
            return null;
          }

          const origin = LocationManager.get('origin');
          const hasCategories = categories && categories.length;

          return (
            <React.Fragment>
              {!loading ? (
                <MetaProvider
                  metaSubscriber={META_SUBSCRIBER_NAME.BLOG_LANDING_META}
                  meta={{
                    [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTICLES_LIST]: generateLDJSON(
                      LDTYPES.ARTICLE_CATEGORIES_LIST,
                      categories.map((item) => {
                        const categoryUrl = UrlAssembler.articleCategory(
                          item.uri,
                          ENUM_ARTICLE.type.TYPE_GUIDE,
                          { origin },
                        );
                        return {
                          ...item,
                          url: categoryUrl,
                          mainEntityOfPage: {
                            type: 'WebPage',
                            url: categoryUrl,
                          },
                        };
                      }),
                    ),
                  }}
                  hasError={!!error || !data}
                />
              ) : null}
              {loading ? <IsomorphicRipple isActive isFullScreen /> : null}
              {hasCategories ? (
                <React.Fragment>
                  <Section padding="none">
                    <Wrapper>
                      <Breadcrumbs
                        links={[
                          {
                            isLink: false,
                            name: formatMessage({ id: 'components.breadcrumbs.guides' }),
                            url: UrlAssembler.guideList(),
                          },
                        ]}
                      />
                      <BlogHeader
                        title={formatMessage({ id: 'components.guides.landingTitle' })}
                        description={formatMessage({ id: 'components.guides.landingDescription' })}
                        categories={categories}
                      >
                        <Boxes>
                          {categories.map(({ id, uri, type, name }) => (
                            <Button
                              key={id}
                              type="secondary"
                              isAnchor
                              to={UrlAssembler.articleCategory(uri, type)}
                            >
                              {name}
                            </Button>
                          ))}
                        </Boxes>
                      </BlogHeader>
                    </Wrapper>
                  </Section>
                  <div ref={topOfGridRef} />
                  <Section>
                    <Wrapper
                      sizes={{
                        xs: 'wide',
                        sm: 'wide',
                        md: 'wide',
                        lg: 'narrow',
                        xl: 'narrow',
                      }}
                    >
                      <Query
                        query={FILTER_ARTICLE_CATEGORIES}
                        variables={{
                          inputArticleCategoryFilter: {
                            type: ENUM_ARTICLE.type.TYPE_GUIDE,
                            page,
                            items: 4,
                          },
                        }}
                      >
                        {({ loading: loadingPaginatedCategories, data, error }) => {
                          const {
                            filterArticleCategories: { items: categories, pagination = {} } = {},
                          } = data || {};

                          if (error) {
                            return null;
                          }

                          const hasPaginatedCategories = categories && categories.length;

                          if (!hasPaginatedCategories) {
                            return null;
                          }

                          const BASE_FILTER_ARTICLES_INPUT = {
                            items: 3,
                            page: 1,
                            type: ENUM_ARTICLE.type.TYPE_GUIDE,
                            showcase: true,
                          };

                          const categoriesQueryVars = hasPaginatedCategories
                            ? categories.reduce((aggregator, item, idx) => {
                                aggregator[`inputArticleFilter${idx}`] = {
                                  ...BASE_FILTER_ARTICLES_INPUT,
                                  ...(categories[idx] && categories[idx].uri
                                    ? { category: categories[idx].uri }
                                    : {}),
                                };

                                return aggregator;
                              }, {})
                            : null;
                          const filterArticlesByCategoryQuery = hasPaginatedCategories
                            ? [
                                FILTER_ARTICLES_QUERY_ONE,
                                FILTER_ARTICLES_QUERY_TWO,
                                FILTER_ARTICLES_QUERY_THREE,
                                FILTER_ARTICLES_QUERY_FOUR,
                              ][hasPaginatedCategories - 1] || FILTER_ARTICLES_QUERY_ONE
                            : null;

                          return (
                            <Query
                              query={filterArticlesByCategoryQuery}
                              variables={categoriesQueryVars}
                            >
                              {({ loading: loadingCategoryArticles, data, error }) => {
                                if (error) {
                                  return null;
                                }

                                return (
                                  <IsomorphicRipple
                                    isActive={loadingCategoryArticles || loadingPaginatedCategories}
                                  >
                                    <GridSquare columnCount={2} justify="space-between">
                                      {categories.map(({ id, uri, type, name }, key) => {
                                        const { items: articles } =
                                          (data && data[`list${key}`]) || {};

                                        return (
                                          <GridSquareItem key={id} columnCount={2}>
                                            <DataProviderPreloaderPage
                                              uniqueId={PRELOADER_SUBSCRIBE_ID}
                                              attach={
                                                loadingPaginatedCategories ||
                                                loadingCategoryArticles
                                              }
                                            />
                                            <Heading tag="span" level="3">
                                              {name}
                                            </Heading>
                                            {articles && articles.length
                                              ? articles.map(
                                                  ({ id, type, slug, title, summary, images }) => {
                                                    const articleUrl = UrlAssembler.articleDetail(
                                                      id,
                                                      slug,
                                                      type,
                                                    );
                                                    return (
                                                      <CardArticle
                                                        key={id}
                                                        displayStyle="3reversed"
                                                        titleTag="h2"
                                                        className={guidesHomeArticleCls}
                                                        title={
                                                          <Link to={articleUrl} title={title}>
                                                            {title}
                                                          </Link>
                                                        }
                                                        description={summary}
                                                        share={
                                                          <Share
                                                            theme="dark"
                                                            contentKey="article"
                                                            contentValues={{ title }}
                                                            image={generateSrcForCDN(
                                                              extractImageFileFromData.articleImage(
                                                                images,
                                                              ),
                                                            )}
                                                            url={`${origin}${articleUrl}`}
                                                          />
                                                        }
                                                      >
                                                        <Link to={articleUrl} title={title}>
                                                          <PicturePlaceholder
                                                            type="slider.gridLarge"
                                                            artDirectionKey="article.main"
                                                            image={extractImageFileFromData.articleImage(
                                                              images,
                                                            )}
                                                            alt={title}
                                                            lazyload={LAZYLOAD_ARTICLE_CONFIG}
                                                          />
                                                        </Link>
                                                      </CardArticle>
                                                    );
                                                  },
                                                )
                                              : null}
                                            <div className={guidesHomeActionCls}>
                                              <Button
                                                isAnchor
                                                to={UrlAssembler.articleCategory(uri, type)}
                                              >
                                                {formatMessage({ id: 'common.viewMore' })}
                                              </Button>
                                            </div>
                                          </GridSquareItem>
                                        );
                                      })}
                                    </GridSquare>
                                    {categories && categories.length ? (
                                      <GridPaginationWrapper>
                                        <Pagination
                                          paginationType={ENUM_PAGINATION.type.SLIDER}
                                          totalPages={pagination.totalPages}
                                          total={pagination.totalItems}
                                          pageSize={pagination.itemsPerPage}
                                          current={pagination.currentPage}
                                          beforeChange={scrollToTopOfGrid}
                                        >
                                          {(props) => <SliderPagination {...props} />}
                                        </Pagination>
                                      </GridPaginationWrapper>
                                    ) : null}
                                  </IsomorphicRipple>
                                );
                              }}
                            </Query>
                          );
                        }}
                      </Query>
                    </Wrapper>
                  </Section>
                </React.Fragment>
              ) : null}
            </React.Fragment>
          );
        }}
      </Query>
    </LayoutDefault>
  );
}
